import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Key, Copy, Calendar, Clock, Puzzle } from 'lucide-react';
import toast from 'react-hot-toast';
import { userLicencesAPI } from '../utils/api';
import LicenseViewModal from '../components/LicenseViewModal';
import { useAuth } from '../context/AuthContext';

const formatDate = (dateStr, locale) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString(locale === 'az' ? 'en-US' : locale, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch (e) {
    return dateStr;
  }
};

const UserMyLicenses = () => {
  const { t, i18n } = useTranslation();
  const { user: authUser } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const fetchLicenses = async () => {
      try {
        const response = await userLicencesAPI.getMyLicences();
        setLicenses(response.data.data || []);
      } catch (error) {
        toast.error(t('errors.serverError'));
      } finally {
        setLoading(false);
      }
    };
    fetchLicenses();
  }, [t]);

  const handleCopy = async (license) => {
    try {
      await navigator.clipboard.writeText(license.licenseKey);
      setCopiedId(license._id);
      toast.success(t('myLicenses.copied'));
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      toast.error(t('errors.serverError'));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('myLicenses.title')}</h1>
        <p className="text-gray-500 text-sm mt-1">{t('myLicenses.subtitle')}</p>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
          <p className="text-gray-500 mt-2">{t('common.loading')}</p>
        </div>
      ) : licenses.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
          <Key className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('myLicenses.empty.title')}
          </h3>
          <p className="text-sm text-gray-500">{t('myLicenses.empty.hint')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {t('myLicenses.table.remainingDays')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <Puzzle className="w-3 h-3" />
                      {t('myLicenses.table.plugin')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <Key className="w-3 h-3" />
                      {t('myLicenses.table.licenseKey')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {t('myLicenses.table.createdAt')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {licenses.map((license) => (
                  <tr
                    key={license._id}
                    onClick={() => setSelectedLicense(license)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                          license.remainingDays === 0
                            ? 'bg-red-100 text-red-700'
                            : license.remainingDays <= 7
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {license.remainingDays ?? 0} {t('myLicenses.days', { count: license.remainingDays ?? 0 })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {license.plugin?.name || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded border border-gray-200">
                          {license.licenseKey}
                        </code>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(license);
                          }}
                          className="text-gray-400 hover:text-primary-600 transition-colors"
                          title={t('myLicenses.copied')}
                        >
                          <Copy
                            className={`w-4 h-4 ${
                              copiedId === license._id ? 'text-green-600' : ''
                            }`}
                          />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatDate(license.createdAt, i18n.language)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedLicense && (
        <LicenseViewModal
          license={{
            ...selectedLicense,
            user: authUser
              ? { firstName: authUser.firstName, lastName: authUser.lastName }
              : null,
          }}
          onClose={() => setSelectedLicense(null)}
          hideTechnicalFields
        />
      )}
    </div>
  );
};

export default UserMyLicenses;